/**
 * @file SearchComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de busqueda utilizando un debounce de x ms para evitar llamadas innecesarias al back
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-04-28
 * @author DLTCode
 * @author Mohammed Derfoufi
 * @copyright © DLTCode
 * @license Private
 * */

import { SearchComponentProps } from "@/types/ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { debounce, DebouncedFunc } from "lodash";
import createFormData from "@/lib/createFormData";
import { SEARCH_DEBOUNCE_TIME } from "@/constants/searchDebounceTime";
import { Search, X } from "lucide-react";
export default function SearchComponent<TFormData extends Record<string, unknown>>({
  formData,
  setFormData,
  searchAttribute = "",
  onSearch,
  debounceTime = SEARCH_DEBOUNCE_TIME,
  placeholder,
  className
}: SearchComponentProps<TFormData>) {
  const isInitialMount = useRef(true);
  const debouncedSearchRef = useRef<DebouncedFunc<
  // eslint-disable-next-line no-unused-vars
  (term: string) => void> | null>(null);
  const searchValue = useMemo(() => String(formData[searchAttribute] || ""), [formData, searchAttribute]);
  const previousSearchValue = useRef(searchValue);
  useEffect(() => {
    debouncedSearchRef.current = debounce((term: string) => {
      const updatedFormData = {
        ...formData,
        [searchAttribute]: term
      };
      onSearch(createFormData(updatedFormData));
    }, debounceTime);
    return () => {
      debouncedSearchRef.current?.cancel();
    };
  }, [debounceTime, onSearch, formData, searchAttribute]);
  useEffect(() => {
    if (!isInitialMount.current && searchValue !== previousSearchValue.current) {
      debouncedSearchRef.current?.(searchValue);
    }
    previousSearchValue.current = searchValue;
  }, [searchValue]);
  useEffect(() => {
    isInitialMount.current = false;
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      [searchAttribute]: newValue
    }));
  };
  const handleKeyDown = (e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_0.key === "Enter") {
      e_0.preventDefault();
      performSearch();
    }
  };
  const performSearch = useCallback(() => {
    onSearch(createFormData(formData));
  }, [formData, onSearch]);
  const handleClearSearch = useCallback(() => {
    debouncedSearchRef.current?.cancel();
    setFormData(prev_0 => ({
      ...prev_0,
      [searchAttribute]: ""
    }));
    onSearch(createFormData({
      ...formData,
      [searchAttribute]: ""
    }));
  }, [formData, onSearch, searchAttribute, setFormData]);
  return <section className={className ? `search-component ${className}` : "search-component"}>
      <label htmlFor={String(searchAttribute)} className="sr-only">
        {placeholder}
      </label>
      <input id={String(searchAttribute)} name={String(searchAttribute)} type="search" className="search-component__input" placeholder={placeholder} value={searchValue} onChange={handleChange} onKeyDown={handleKeyDown} />
      <button type="button" className="search-component__button" aria-label={placeholder} onClick={searchValue ? handleClearSearch : performSearch}>
        {searchValue ? <X size={24} role="img" aria-hidden="true" /> : <Search size={24} role="img" aria-hidden="true" />}
      </button>
    </section>;
}